import { useState, useEffect, useMemo } from 'react'
import { useApi } from '@/hooks/useApi'
import type { DestinatarioProyecto } from '@/lib/types'
import { Button, Modal } from '@/components/ui'
import { IconSend, IconX, IconAlertTriangle } from '@/components/icons/Icons'

/*
  Escribirle al cliente sin que exista un hilo previo — una renovación, una
  cobranza, un aviso.

  El candado: no hay campo para teclear una dirección. Los destinatarios son
  los contactos de ESTA ficha y nada más, y el backend vuelve a
  validarlo. Se puede empezar una conversación, pero no con cualquiera.

  Y hay confirmación antes de enviar: el correo sale con el nombre de la
  empresa hacia un cliente y no se puede deshacer.
*/
interface Props {
  basePath: string
  /** Cómo se llama la ficha en los textos: «proyecto», «oportunidad». */
  nombreFicha: string
  open: boolean
  onClose: () => void
  onEnviado: () => void
}

function etiqueta(d: DestinatarioProyecto): string {
  const nombre = [d.nombre, d.apellido].filter(Boolean).join(' ').trim()
  return nombre || d.email || 'Sin nombre'
}

export default function NuevoCorreoModal({ basePath, nombreFicha, open, onClose, onEnviado }: Props) {
  const api = useApi()
  const [contactos, setContactos] = useState<DestinatarioProyecto[]>([])
  const [cargando, setCargando] = useState(false)
  const [para, setPara] = useState<string[]>([])
  const [cc, setCc] = useState<string[]>([])
  const [asunto, setAsunto] = useState('')
  const [cuerpo, setCuerpo] = useState('')
  const [confirmando, setConfirmando] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // El id se fija al abrir, no al enviar: así un segundo clic manda el mismo id
  // y el backend lo reconoce en vez de mandar el correo dos veces.
  const envioId = useMemo(
    () => `nuevo-${basePath}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    [basePath, open], // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    if (!open) return
    setPara([]); setCc([]); setAsunto(''); setCuerpo('')
    setError(null); setConfirmando(false)
    setCargando(true)
    api.get<DestinatarioProyecto[]>(`${basePath}/destinatarios`)
      .then(d => {
        setContactos(d)
        // El contacto principal viene primero y queda elegido: es a quien se
        // le escribe en el 90 % de los casos.
        const principal = d.find(x => x.es_principal && x.email)
        if (principal?.email) setPara([principal.email.toLowerCase()])
      })
      .catch(() => setError('No se pudieron cargar los contactos del cliente.'))
      .finally(() => setCargando(false))
  }, [open, basePath]) // eslint-disable-line react-hooks/exhaustive-deps

  function alternar(dir: string) {
    if (para.includes(dir)) { setPara(para.filter(d => d !== dir)); setCc([...cc, dir]) }
    else if (cc.includes(dir)) setCc(cc.filter(d => d !== dir))
    else setPara([...para, dir])
  }

  async function enviar() {
    setEnviando(true)
    setError(null)
    try {
      await api.post(`${basePath}/nuevo`, {
        para, cc, asunto, cuerpo, envio_id: envioId,
      })
      onClose()
      onEnviado()
    } catch (e: any) {
      setError(e?.message || 'No se pudo enviar el correo.')
      setConfirmando(false)
    } finally {
      setEnviando(false)
    }
  }

  if (!open) return null

  const conCorreo = contactos.filter(d => d.email)
  const sinCorreo = contactos.filter(d => !d.email)

  return (
    <Modal open={open} onClose={onClose} title="Escribir al cliente" width={640}>
      {cargando ? (
        <p className="text-muted">Cargando contactos…</p>
      ) : conCorreo.length === 0 ? (
        // Se dice la causa, en vez de mostrar un formulario que no va a andar.
        <p className="text-muted" style={{ fontSize: 14 }}>
          Esta {nombreFicha} no tiene contactos con correo cargado. Agregalos en su ficha
          y volvé a intentarlo: desde acá solo se le puede escribir a sus contactos.
        </p>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 12 }}>
            <span className="text-muted" style={{ fontSize: 13 }}>Para:</span>
            {conCorreo.map(d => {
              const dir = (d.email as string).toLowerCase()
              const enPara = para.includes(dir)
              const enCc = cc.includes(dir)
              return (
                <button
                  key={d.contacto_id}
                  className={`chip-destino ${enPara ? 'is-para' : enCc ? 'is-cc' : 'is-fuera'}`}
                  title={enPara ? `${dir} — recibe el correo. Clic para pasarlo a copia` : enCc ? `${dir} — va en copia. Clic para sacarlo` : `${dir} — no lo recibe. Clic para agregarlo`}
                  onClick={() => alternar(dir)}
                >
                  {etiqueta(d)}
                </button>
              )
            })}
          </div>

          {sinCorreo.length > 0 && (
            <p className="text-muted" style={{ fontSize: 12.5, marginTop: -6, marginBottom: 10 }}>
              Sin correo cargado, por eso no aparecen: {sinCorreo.map(etiqueta).join(', ')}.
            </p>
          )}

          <input
            type="text"
            className="form-input"
            placeholder="Asunto"
            value={asunto}
            onChange={e => setAsunto(e.target.value)}
            style={{ width: '100%', marginBottom: 10 }}
          />

          <textarea
            className="form-input"
            rows={9}
            placeholder="Escribí el mensaje…"
            value={cuerpo}
            onChange={e => setCuerpo(e.target.value)}
            style={{ width: '100%', resize: 'vertical' }}
          />

          <p className="text-muted" style={{ fontSize: 12.5, marginTop: 6 }}>
            Sale desde el buzón conectado, con la firma de la empresa. El hilo queda archivado
            en esta {nombreFicha}.
          </p>

          {error && (
            <p style={{ color: 'var(--danger)', fontSize: 13, marginTop: 8 }}>
              <IconAlertTriangle size={13} /> {error}
            </p>
          )}

          {confirmando ? (
            <div style={{ marginTop: 12, padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
              <p style={{ fontSize: 14, marginBottom: 4 }}>
                <b>Se va a enviar a:</b> {para.join(', ') || '(nadie)'}
                {cc.length > 0 && <> · <b>copia:</b> {cc.join(', ')}</>}
              </p>
              <p className="text-muted" style={{ fontSize: 13, marginBottom: 10 }}>
                El correo sale ahora y no se puede deshacer.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button onClick={enviar} disabled={enviando}>
                  <IconSend size={14} /> {enviando ? 'Enviando…' : 'Confirmar y enviar'}
                </Button>
                <button className="btn btn-secondary" onClick={() => setConfirmando(false)} disabled={enviando}>
                  Volver a editar
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <Button
                onClick={() => setConfirmando(true)}
                disabled={!asunto.trim() || !cuerpo.trim() || para.length === 0}
              >
                <IconSend size={14} /> Revisar y enviar
              </Button>
              <button className="btn btn-secondary" onClick={onClose}>
                <IconX size={14} /> Cancelar
              </button>
            </div>
          )}
        </>
      )}
    </Modal>
  )
}
